import React from 'react';
import useChallengeCountdown from 'hooks/useChallengeCountdown';
import { useSortedFeaturedItems } from 'hooks/useSortedFeaturedItems';
import { IonPage, IonContent, IonIcon, useIonRouter } from '@ionic/react';
import { chevronDownOutline, chevronForwardOutline } from 'ionicons/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { SlotReduxCommandCreator } from 'appRedux/actions/slotCommandCreator';
import EventCard from 'pages/EventsTab/EventCard';
import { openChallengeExplore } from 'pages/EventsTab/Challenges/openChallengeExplore';
import { CasinoService, IChallengeItem } from 'services/casino';
import LeaderboardTopLayer from 'pages/LeaderBoardTab/LeaderboardTopLayer';
import { IState } from 'appRedux/createStore';
import { casinoIdListEnum } from 'common/commonEnum';
import ListSheetLeaderboard from 'pages/LeaderBoardTab/ListSheetLeaderboard';
import { SnapPoint } from 'components/CasinoTabBar/views/home/views/MapStats/HotListReport/components/ListSheet';
import FloorMapBackdrop from '../FloorMapBackdrop';
import CasinoNavBar from 'components/CasinoNavBar';
import LinkPlayerWrapper from 'pages/MyPlayerCards/LinkPlayerWrapper';
import FooterLogo from 'components/FooterLogo';

const Challenges: React.FC = () => {
  const casinoSvcRef = React.useRef<CasinoService>(new CasinoService());
  const ionRouter = useIonRouter();
  const history = useHistory();
  const dispatch = useDispatch();
  const slotCommands = SlotReduxCommandCreator(dispatch);
  const casinoId = useSelector<IState>((s) => s.app.persistedState.casinoSchema?.kpCasinoPk) as
    | number
    | undefined;
  const isPlayerLinked = useSelector<IState>(
    (s) => s.app.persistedState.linkPlayerState?.isPlayerLinked
  ) as boolean;
  const isCasinoAccount = useSelector<IState>(
    (s) => s.app.persistedState.userInfo?.isCasinoAccount
  ) as boolean;
  const isSlotcheckInternal = useSelector<IState>(
    (s) => s.app.persistedState.userInfo?.groups?.includes('SlotcheckInternal')
  ) as boolean;
  const hasAccess = Boolean(isCasinoAccount || isPlayerLinked || isSlotcheckInternal);
  const casinoBackgroundClass = React.useMemo(() => {
    switch (Number(casinoId)) {
      case casinoIdListEnum.LOTC:
        return 'casino-bg--lotc';
      case casinoIdListEnum.EUREKA:
        return 'casino-bg--eureka';
      case casinoIdListEnum.RWC:
        return 'casino-bg--rwc';
      case casinoIdListEnum.SSC:
      case casinoIdListEnum.ACH:
        return 'casino-bg--ssc';
      default:
        return 'casino-bg--default';
    }
  }, [casinoId]);
  const [featuredItems, setFeaturedItems] = React.useState<IChallengeItem[]>([]);
  const [others, setOthers] = React.useState<IChallengeItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const carouselRef = React.useRef<HTMLDivElement>(null);

  const isPrivilegedAccount = isCasinoAccount || isSlotcheckInternal;

  const sortedFeaturedItems = useSortedFeaturedItems(featuredItems);
  const featured = sortedFeaturedItems[0] ?? null;

  React.useEffect(() => {
    if (!casinoId) {
      setFeaturedItems([]);
      setOthers([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const sub = casinoSvcRef.current.GetChallenges(casinoId, isPrivilegedAccount).subscribe({
      next: (resp) => {
        const featuredList = Array.isArray(resp?.featured) ? resp.featured : [];
        const othersList = Array.isArray(resp?.others) ? resp.others : [];
        setFeaturedItems(featuredList);
        setOthers(othersList);
        setIsLoading(false);
      },
      error: () => {
        setFeaturedItems([]);
        setOthers([]);
        setIsLoading(false);
      },
    });

    return () => sub.unsubscribe();
  }, [casinoId, isPrivilegedAccount]);

  const { activeChallenges, futureChallenges, completedChallenges } = React.useMemo(() => {
    const now = new Date();
    return {
      activeChallenges: others.filter((c) => {
        if (c.active === false) return false;
        const started = !c.startDate || now >= new Date(c.startDate);
        const notEnded = !c.endDate || now <= new Date(c.endDate);
        return started && notEnded;
      }),
      futureChallenges: others.filter(
        (c) => c.active !== false && !!c.startDate && now < new Date(c.startDate)
      ),
      completedChallenges: others.filter(
        (c) => c.active === false || (!!c.endDate && now > new Date(c.endDate))
      ),
    };
  }, [others]);

  const handleCarouselScroll = () => {
    const el = carouselRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    setActiveIndex(idx);
  };

  const [activeOpen, setActiveOpen] = React.useState(true);
  const [upcomingOpen, setUpcomingOpen] = React.useState(true);
  const [completedOpen, setCompletedOpen] = React.useState(true);

  const { state: countdownState } = useChallengeCountdown(featured);
  const casinoLogoAlt = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.logos?.logoAlt
  ) as string | undefined;
  return (
    <IonPage className={`challenges-page events-tab ${casinoBackgroundClass}`}>
      <IonContent className="leaderboard-tab__content events-tab__content" fullscreen>
        <CasinoNavBar />

        <FloorMapBackdrop />

        <ListSheetLeaderboard initialSnap={SnapPoint.Full}>
          <LeaderboardTopLayer
            title="CHALLENGES"
            showBack={true}
            onBack={() => {
              history.goBack();
            }}
            showBadge={true}
            badgeIcon="challenge"
            activeDotPage={1}
            onDotClick={(page) => {
              if (page === 0) ionRouter.push('/tabs/leaderboard', 'back', 'push');
            }}
            onSwipe={(dir) => {
              if (dir === 'right') ionRouter.push('/tabs/leaderboard', 'back', 'push');
            }}
          />
          {!hasAccess ? (
            <LinkPlayerWrapper IsShowMaybeLater={false} />
          ) : (
            <>
              {!isLoading && featured && countdownState !== 'none' && (
                <div className="events-hero__tabs">
                  <span className="events-hero__tab events-hero__tab--active">
                    Point Challenges
                  </span>
                  <div className="event-underline" />
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px',
                  alignItems: 'center',
                }}
              >
                {isLoading && (
                  <div
                    className="leaderboard-mapstats__loader leaderboard-mapstats__loader--challenge"
                    role="status"
                    aria-live="polite"
                  >
                    <span className="leaderboard-table__spinner" aria-hidden="true" />
                    <span>Loading challenges...</span>
                  </div>
                )}

                {!isLoading && sortedFeaturedItems.length > 0 && (
                  <div className="featured-carousel-wrapper">
                    <div
                      className="featured-carousel"
                      ref={carouselRef}
                      onScroll={handleCarouselScroll}
                    >
                      {sortedFeaturedItems.map((item) => (
                        <div key={item.uuid || item._id} className="featured-carousel__slide">
                          <EventCard
                            title={<>{item.name}</>}
                            subtitle={item.description}
                            descriptions={item.descriptions}
                            justJoined={item.joined}
                            participantStatus={item.participantStatus}
                            ctaLabel="Learn More"
                            onCtaClick={() => {
                              const challengeId = String(item?.uuid || item?._id || '');
                              if (challengeId) history.push(`/tabs/join-challenge/${challengeId}`);
                            }}
                            onExploreClick={() => openChallengeExplore(slotCommands, history, item, null, casinoId)}
                            casinoLogoSrc={casinoLogoAlt}
                            variant="feature"
                            styleData={item.challenge?.styleData}
                            joined={item.joined ?? false}
                            startDate={item.startDate}
                            endDate={item.endDate}
                            active={item.active}
                          />
                        </div>
                      ))}
                    </div>
                    {sortedFeaturedItems.length > 1 && (
                      <div className="featured-carousel__dots">
                        {sortedFeaturedItems.map((_, i) => (
                          <span
                            key={i}
                            className={`featured-carousel__dot${i === activeIndex ? ' featured-carousel__dot--active' : ''}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!isLoading && (
                  <>
                    {activeChallenges.length > 0 && (
                      <>
                        <button
                          className={`challenges-section-header challenges-section-header--toggle${!activeOpen ? ' challenges-section-header--closed' : ''}`}
                          onClick={() => setActiveOpen((v) => !v)}
                        >
                          Now Playing
                          <IonIcon icon={activeOpen ? chevronDownOutline : chevronForwardOutline} />
                        </button>
                        {activeOpen && activeChallenges.map((c) => (
                          <EventCard
                            key={c.uuid || c._id}
                            title={<>{c.name}</>}
                            subtitle={c.description}
                            descriptions={c.descriptions}
                            justJoined={c.joined}
                            participantStatus={c.participantStatus}
                            variant="feature"
                            ctaLabel="Learn More"
                            onCtaClick={() => {
                              const challengeId = String(c?.uuid || c?._id || '');
                              if (challengeId) history.push(`/tabs/join-challenge/${challengeId}`);
                            }}
                            onExploreClick={() => openChallengeExplore(slotCommands, history, c, null, casinoId)}
                            casinoLogoSrc={casinoLogoAlt}
                            backslash={false}
                            styleData={c.challenge?.styleData}
                            joined={c.joined ?? false}
                            startDate={c.startDate}
                            endDate={c.endDate}
                            active={c.active}
                          />
                        ))}
                      </>
                    )}
                    {futureChallenges.length > 0 && (
                      <>
                        <button
                          className={`challenges-section-header challenges-section-header--toggle${!upcomingOpen ? ' challenges-section-header--closed' : ''}`}
                          onClick={() => setUpcomingOpen((v) => !v)}
                        >
                          Upcoming
                          <IonIcon icon={upcomingOpen ? chevronDownOutline : chevronForwardOutline} />
                        </button>
                        {upcomingOpen && futureChallenges.map((c) => (
                          <EventCard
                            key={c.uuid || c._id}
                            title={<>{c.name}</>}
                            subtitle={c.description}
                            descriptions={c.descriptions}
                            justJoined={c.joined}
                            participantStatus={c.participantStatus}
                            variant="feature"
                            ctaLabel="Learn More"
                            onCtaClick={() => {
                              const challengeId = String(c?.uuid || c?._id || '');
                              if (challengeId) history.push(`/tabs/join-challenge/${challengeId}`);
                            }}
                            onExploreClick={() => openChallengeExplore(slotCommands, history, c, null, casinoId)}
                            casinoLogoSrc={casinoLogoAlt}
                            backslash={false}
                            styleData={c.challenge?.styleData}
                            joined={c.joined ?? false}
                            startDate={c.startDate}
                            endDate={c.endDate}
                            active={c.active}
                          />
                        ))}
                      </>
                    )}
                    {completedChallenges.length > 0 && (
                      <>
                        <button
                          className={`challenges-section-header challenges-section-header--toggle${!completedOpen ? ' challenges-section-header--closed' : ''}`}
                          onClick={() => setCompletedOpen((v) => !v)}
                        >
                          Completed
                          <IonIcon icon={completedOpen ? chevronDownOutline : chevronForwardOutline} />
                        </button>
                        {completedOpen && completedChallenges.map((c) => (
                          <EventCard
                            key={c.uuid || c._id}
                            title={<>{c.name}</>}
                            subtitle={c.description}
                            descriptions={c.descriptions}
                            justJoined={c.joined}
                            participantStatus={c.participantStatus}
                            variant="feature"
                            ctaLabel="Learn More"
                            onCtaClick={() => {
                              const challengeId = String(c?.uuid || c?._id || '');
                              if (challengeId) history.push(`/tabs/join-challenge/${challengeId}`);
                            }}
                            onExploreClick={() => openChallengeExplore(slotCommands, history, c, null, casinoId)}
                            casinoLogoSrc={casinoLogoAlt}
                            backslash={false}
                            styleData={c.challenge?.styleData}
                            joined={c.joined ?? false}
                            startDate={c.startDate}
                            endDate={c.endDate}
                            active={c.active}
                          />
                        ))}
                      </>
                    )}
                    {activeChallenges.length === 0 && futureChallenges.length === 0 && completedChallenges.length === 0 && (
                      <div className="challenges-empty" style={{ marginTop: 24 }}>
                        More challenges coming soon!
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </ListSheetLeaderboard>

        <FooterLogo />
      </IonContent>
    </IonPage>
  );
};

export default Challenges;
